import { Chip, makeStyles, TextField, Typography } from "@material-ui/core";
import { Autocomplete } from "@material-ui/lab";
import React, { useMemo } from "react";
import { useSysadminData } from "../SysadminData";

const useStyles = makeStyles(theme => ({
    chips: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
    },
    option: {
        display: "flex",
        flexDirection: "column",
    },
}));

interface Props {
    value: string[];
    onChange?: (value: string[]) => void;
    readOnly?: boolean;
    label?: string;
}

/** Picks admins among existing application users (admin ids are application user ids). */
export const AdminPicker: React.FC<Props> = ({ value, onChange, readOnly, label = "Admins" }) => {
    const classes = useStyles();
    const { serviceUsers, loading } = useSysadminData();

    const groupsByUser = useMemo(
        () => new Map(serviceUsers.map(user => [user.content.id, user.content.data_raw?.groups ?? []])),
        [serviceUsers]
    );
    const options = useMemo(() => {
        const ids = new Set<string>(serviceUsers.map(user => user.content.id));
        value.forEach(id => ids.add(id));
        return Array.from(ids).sort((a, b) => a.localeCompare(b));
    }, [serviceUsers, value]);

    if (readOnly) {
        return value.length === 0
            ? <Typography color="textSecondary">No admins</Typography>
            : <div className={classes.chips}>{value.map(id => <Chip key={id} label={id} />)}</div>;
    }

    return (
        <Autocomplete
            multiple
            filterSelectedOptions
            loading={loading}
            options={options}
            value={value}
            onChange={(_, newValue) => onChange && onChange(newValue as string[])}
            renderOption={option => (
                <div className={classes.option}>
                    <span>{option}</span>
                    <Typography variant="caption" color="textSecondary">
                        {(groupsByUser.get(option) ?? []).join(", ") || "Unknown user"}
                    </Typography>
                </div>
            )}
            renderTags={(selected, getTagProps) =>
                selected.map((option, index) => (
                    <Chip
                        label={option}
                        color={groupsByUser.has(option) ? "default" : "secondary"}
                        {...getTagProps({ index })}
                    />
                ))
            }
            renderInput={params => (
                <TextField
                    {...params}
                    variant="outlined"
                    label={label}
                    placeholder={value.length === 0 ? "Search users by email" : ""}
                    required
                    error={value.length === 0}
                    helperText={value.length === 0 ? "At least one admin is required" : undefined}
                />
            )}
        />
    );
};
